import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { api } from "../../api/client";
import css from "./dcSections.module.css";

type CtaKind = "special" | "quest" | "lead";
interface PersonalOfferRow {
  id: string;
  active: number;
  sort_order: number;
  kicker: string;
  title: string;
  description: string;
  badge: string;
  photos: string[];
  cta_kind: CtaKind;
  cta_target: string;
  cta_label: string;
  lead_enabled: number;
  audience: { type: string };
}

const lbl: CSSProperties = { display: "block", fontSize: 10.5, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--text-2)", margin: "0 0 4px" };
const inp: CSSProperties = { width: "100%", boxSizing: "border-box", padding: "8px 10px", background: "var(--surface-1)", border: "1px solid var(--surface-3)", borderRadius: 7, color: "var(--text-1)", fontSize: 13, fontFamily: "inherit" };
const area: CSSProperties = { ...inp, minHeight: 74, resize: "vertical", lineHeight: 1.45 };
const card: CSSProperties = { border: "1px solid var(--surface-3)", borderRadius: 9, padding: 11, margin: "0 0 10px", background: "var(--surface-1)" };
const row: CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };
const miniBtn: CSSProperties = { padding: "6px 11px", borderRadius: 7, border: "1px solid var(--surface-3)", background: "var(--surface-2)", color: "var(--text-2)", fontSize: 12, cursor: "pointer" };
const saveBar: CSSProperties = { position: "sticky", bottom: 0, display: "flex", justifyContent: "flex-end", gap: 8, padding: "10px 0", marginTop: 4, background: "var(--surface-1)", borderTop: "1px solid var(--surface-3)" };

const phasmoPhotos = ["/specver/phasmo_cover.webp", "/specver/phasmo_g1.webp", "/specver/phasmo_g2.webp", "/specver/phasmo_g3.webp", "/specver/phasmo_g4.webp"];
const emptyOffer = (): PersonalOfferRow => ({
  id: `offer-${Date.now()}`,
  active: 1,
  sort_order: 0,
  kicker: "ЛИЧНОЕ ОТКРЫТИЕ",
  title: "",
  description: "",
  badge: "",
  photos: [],
  cta_kind: "lead",
  cta_target: "",
  cta_label: "Подробнее",
  lead_enabled: 1,
  audience: { type: "all" },
});

export function DcPersonalOffersSection({ onToast, onOpenLeads }: { onToast: (m: string) => void; onOpenLeads: (offerId: string) => void }) {
  const [offers, setOffers] = useState<PersonalOfferRow[]>([]);
  const [original, setOriginal] = useState<PersonalOfferRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reload = () => {
    setLoading(true); setError("");
    api<{ offers: PersonalOfferRow[] }>("/api/commerce/personal-offers")
      .then((data) => { setOffers(data.offers ?? []); setOriginal(data.offers ?? []); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Ошибка"))
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);
  const dirty = useMemo(() => JSON.stringify(offers) !== JSON.stringify(original), [offers, original]);
  const patch = (index: number, next: Partial<PersonalOfferRow>) => setOffers((list) => list.map((item, i) => i === index ? { ...item, ...next } : item));
  const add = () => setOffers((list) => [...list, { ...emptyOffer(), sort_order: list.length }]);
  const save = () => {
    if (!dirty || busy) return;
    setBusy(true); setError("");
    api<{ ok: boolean; offers: PersonalOfferRow[] }>("/api/commerce/personal-offers", { method: "PUT", body: { offers } })
      .then(() => { onToast("Офферы сохранены"); reload(); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Ошибка сохранения"))
      .finally(() => setBusy(false));
  };

  if (loading) return <div className={css.main}><div className={css.loading}>Загрузка…</div></div>;
  return <div className={css.main}>
    <div style={{ padding: "10px 12px 28px" }}>
      <p style={{ margin: "0 0 14px", color: "var(--text-2)", fontSize: 12, lineHeight: 1.5 }}>
        Персональные офферы выбираются сервером по аудитории и приходят клиенту в поле commerce.personalOffer. Сейчас активен таргет «Всем», остальные правила подготовлены, но выключены.
      </p>
      {error ? <div className={css.err} style={{ marginBottom: 10 }}>{error}</div> : null}
      {offers.map((offer, index) => <section key={offer.id} style={card}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-1)", fontSize: 13 }}><input type="checkbox" checked={!!offer.active} onChange={(e) => patch(index, { active: e.target.checked ? 1 : 0 })} /> active</label>
          <input style={{ ...inp, width: 72 }} type="number" value={offer.sort_order} onChange={(e) => patch(index, { sort_order: Number(e.target.value) || 0 })} />
          <button type="button" style={{ ...miniBtn, marginLeft: "auto" }} onClick={() => onOpenLeads(offer.id)}>Лиды</button>
          <button type="button" style={{ ...miniBtn, color: "var(--danger, #ff6b6b)" }} onClick={() => setOffers((list) => list.filter((_, i) => i !== index))}>Удалить</button>
        </div>
        <div style={row}>
          <label><span style={lbl}>ID</span><input style={inp} value={offer.id} onChange={(e) => patch(index, { id: e.target.value })} /></label>
          <label><span style={lbl}>Kicker</span><input style={inp} value={offer.kicker} onChange={(e) => patch(index, { kicker: e.target.value })} /></label>
        </div>
        <label style={{ display: "block", marginTop: 10 }}><span style={lbl}>Заголовок</span><input style={inp} value={offer.title} onChange={(e) => patch(index, { title: e.target.value })} /></label>
        <label style={{ display: "block", marginTop: 10 }}><span style={lbl}>Описание</span><textarea style={area} value={offer.description} onChange={(e) => patch(index, { description: e.target.value })} /></label>
        <div style={row}>
          <label><span style={lbl}>Badge</span><input style={inp} value={offer.badge} onChange={(e) => patch(index, { badge: e.target.value })} /></label>
          <label><span style={lbl}>CTA label</span><input style={inp} value={offer.cta_label} onChange={(e) => patch(index, { cta_label: e.target.value })} /></label>
        </div>
        <div style={row}>
          <label><span style={lbl}>CTA kind</span><select style={inp} value={offer.cta_kind} onChange={(e) => patch(index, { cta_kind: e.target.value as CtaKind })}><option value="special">special</option><option value="quest">quest</option><option value="lead">lead</option></select></label>
          <label><span style={lbl}>CTA target</span><input style={inp} value={offer.cta_target} placeholder="phasmo / durk" onChange={(e) => patch(index, { cta_target: e.target.value })} /></label>
        </div>
        <label style={{ display: "block", marginTop: 10 }}><span style={lbl}>Фото, по одному URL в строке</span><textarea style={area} value={offer.photos.join("\n")} onChange={(e) => patch(index, { photos: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} /></label>
        <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
          <button type="button" style={miniBtn} onClick={() => patch(index, { photos: phasmoPhotos })}>Подставить /specver/phasmo</button>
          <label style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-2)", fontSize: 12 }}><input type="checkbox" checked={!!offer.lead_enabled} onChange={(e) => patch(index, { lead_enabled: e.target.checked ? 1 : 0 })} /> lead enabled</label>
          <label style={{ marginLeft: "auto", color: "var(--text-2)", fontSize: 12 }}>Аудитория <select style={{ ...inp, width: 160, marginLeft: 6 }} value={offer.audience?.type ?? "all"} onChange={() => patch(index, { audience: { type: "all" } })}><option value="all">Всем</option><option disabled>Игравшим — скоро</option><option disabled>Не игравшим — скоро</option><option disabled>Фанам — скоро</option></select></label>
        </div>
      </section>)}
      <button type="button" style={miniBtn} onClick={add}>+ Добавить оффер</button>
      <div style={saveBar}><button type="button" style={{ ...miniBtn, background: dirty ? "var(--role)" : "var(--surface-3)", color: dirty ? "#06121a" : "var(--text-3)", fontWeight: 700 }} disabled={!dirty || busy} onClick={save}>{busy ? "Сохранение…" : "Сохранить"}</button></div>
    </div>
  </div>;
}
